import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "children", "disabled", "searchString", "divider", "onClick", "isActive", "LeftIconComponent", "RightIconComponent"];
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { default as withStyles } from '../styles';
import Divider from '../Divider/Divider';
import ListItem from '../List/ListItem';
import ListItemText from '../List/ListItemText';
import classNames from 'classnames';
import { colorTextNeutral500, spacing40 } from '../styles/tokens';
import isNil from 'lodash/isNil';

var styles = function styles(theme) {
  return {
    item: {
      outline: 'none',
      cursor: 'pointer',
      '&:active': {
        '& mark': {
          color: 'white'
        }
      }
    },
    divider: {
      margin: '0 !important',
      backgroundColor: theme.palette.grey['300'] + " !important"
    },
    mark: {
      color: colorTextNeutral500,
      backgroundColor: 'initial'
    },
    disabled: {
      cursor: 'unset'
    },
    icons: {
      '& svg': {
        flexShrink: 0,
        fill: theme.palette.grey['500']
      }
    },
    paddingRightWithIcon: {
      paddingRight: spacing40
    },
    paddingLeftWithIcon: {
      paddingLeft: spacing40
    }
  };
}; // Refer following link for more details about escaping regex characters
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions


var escapeRegExp = function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

var SearchItem = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames;

  var classes = props.classes,
      childrenProp = props.children,
      disabled = props.disabled,
      searchString = props.searchString,
      divider = props.divider,
      onClick = props.onClick,
      isActive = props.isActive,
      LeftIconComponent = props.LeftIconComponent,
      RightIconComponent = props.RightIconComponent,
      rest = _objectWithoutPropertiesLoose(props, _excluded);

  var children = childrenProp;
  var label = children;
  var itemRef = /*#__PURE__*/React.createRef(); // disabling warning for missing dpendency of itemRef
  // refer to the following link for more details on why this is the best option for this case
  // https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook

  useEffect(function () {
    if (isActive) {
      itemRef.current.focus();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isActive]);

  if (children && searchString) {
    var regex = new RegExp(escapeRegExp(searchString), 'gi');
    children = children.replace(regex, function (val) {
      return "<mark class=" + classes.mark + ">" + val + "</mark>";
    });
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ListItem, _extends({
    classes: {
      root: disabled ? classes.disabled : isActive ? classes.itemHover : ''
    },
    className: classNames(classes.item, classes.icons, props.className),
    disabled: disabled,
    "aria-disabled": disabled ? true : false,
    "aria-label": label,
    ref: itemRef,
    onClick: disabled ? null : onClick
  }, rest, {
    tabIndex: 0,
    button: true,
    role: "menuitem"
  }), LeftIconComponent, /*#__PURE__*/React.createElement(ListItemText, {
    classes: {
      root: classNames((_classNames = {}, _classNames[classes.paddingLeftWithIcon] = !isNil(LeftIconComponent), _classNames[classes.paddingRightWithIcon] = !isNil(RightIconComponent), _classNames))
    }
  }, /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: children
    }
  })), RightIconComponent), divider && /*#__PURE__*/React.createElement(Divider, {
    classes: {
      root: classes.divider
    },
    component: "li"
  }));
});
SearchItem.displayName = 'SearchItem';
SearchItem.propTypes = {
  /**
   * The text of the item.
   */
  children: PropTypes.node.isRequired,

  /**
   * If `true`, the item will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * If `true`, a 1px light border is added to the bottom of the list item.
   */
  divider: PropTypes.bool,

  /**
   * Value of this prop would be returned when user clicks on list item.
   */
  option: PropTypes.object,

  /**
   * @ignore
   *
   * Icon to be displayed to the left of the text
   */
  LeftIconComponent: PropTypes.node,

  /**
   * @ignore
   *
   * Icon to be displayed to the right of the text
   */
  RightIconComponent: PropTypes.node
};
export default withStyles(styles, {
  name: 'SearchItem'
})(SearchItem);